import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';
import '../assets/css/notifications.css';

const NotificationsDropdown = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const [selectedNotif, setSelectedNotif] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Close when clicking outside BOTH the trigger and the portaled menu
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        triggerRef.current && triggerRef.current.contains(e.target)
      ) return; // click on trigger handled by toggleDropdown
      if (
        menuRef.current && menuRef.current.contains(e.target)
      ) return; // click inside menu — do nothing
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleDropdown = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth <= 768;
      if (!isMobile) {
        setMenuStyle({
          top: `${rect.bottom + 8}px`,
          right: `${window.innerWidth - rect.right}px`,
        });
      }
    }
    setIsOpen(prev => !prev);
  };

  const handleMarkAsRead = (notificationId, e) => {
    e.stopPropagation();
    markAsRead(notificationId);
  };

  // Open a notification's full detail (and mark it read).
  const handleOpenNotif = (notification) => {
    setSelectedNotif(notification);
    setIsOpen(false);
    const id = notification._id || notification.id;
    if (id && !(notification.read || notification.is_read)) markAsRead(id);
  };

  const handleDelete = (notificationId, e) => {
    e.stopPropagation();
    deleteNotification(notificationId);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order_update':    return 'fa-box';
      case 'new_message':     return 'fa-comment';
      case 'price_alert':     return 'fa-tag';
      case 'payment_update':  return 'fa-credit-card';
      case 'wallet_update':   return 'fa-wallet';
      case 'product_update':  return 'fa-shopping-bag';
      case 'dispute_update':  return 'fa-gavel';
      default:                return 'fa-bell';
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - notifTime) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return notifTime.toLocaleDateString();
  };

  return (
    <div className="notifications-dropdown">
      <button
        ref={triggerRef}
        className="notifications-trigger"
        onClick={toggleDropdown}
        style={{ position: 'relative' }}
      >
        <i className="fas fa-bell"></i>
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && createPortal(
        <>
          {/* Backdrop — clicking it closes the modal */}
          <div className="notifications-backdrop" onClick={() => setIsOpen(false)} />

          {/* Menu — stopPropagation so clicks inside never bubble to backdrop */}
          <div
            ref={menuRef}
            className="notifications-dropdown-menu"
            style={menuStyle}
            onClick={e => e.stopPropagation()}
          >
            <div className="notifications-drag-handle" />

            <div className="notifications-header">
              <h3>
                <i className="fas fa-bell" style={{ color: '#22c55e', marginRight: '0.5rem' }} />
                Notifications
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {unreadCount > 0 && (
                  <button onClick={markAllAsRead} className="mark-all-read">
                    Mark all read
                  </button>
                )}
                <button onClick={() => setIsOpen(false)} className="notifications-close-btn">
                  <i className="fas fa-times" />
                </button>
              </div>
            </div>

            <div className="notifications-list">
              {notifications.length === 0 ? (
                <div className="no-notifications">
                  <i className="fas fa-bell-slash" />
                  <p>No notifications yet</p>
                </div>
              ) : (
                notifications.slice(0, 10).map((notification) => {
                  const notifId = notification._id || notification.id;
                  const isRead = notification.read || notification.is_read;
                  return (
                    <div
                      key={notifId}
                      className={`notification-item ${!isRead ? 'unread' : ''}`}
                      onClick={() => handleOpenNotif(notification)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="notification-icon">
                        <i className={`fas ${getNotificationIcon(notification.type)}`} />
                      </div>
                      <div className="notification-content">
                        <h4 className="notification-title">{notification.title}</h4>
                        <p className="notification-message">{notification.message}</p>
                        <span className="notification-time">
                          {formatTime(notification.timestamp || notification.created_at)}
                        </span>
                      </div>
                      <div className="notification-actions">
                        {!isRead && (
                          <button
                            onClick={(e) => handleMarkAsRead(notifId, e)}
                            className="notification-action-btn"
                            title="Mark as read"
                          >
                            <i className="fas fa-check" />
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDelete(notifId, e)}
                          className="notification-action-btn"
                          title="Delete"
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {notifications.length > 10 && (
              <div className="notifications-footer">
                <button
                  onClick={() => { setIsOpen(false); navigate('/notifications'); }}
                  className="view-all-notifications-btn"
                >
                  View all notifications
                </button>
              </div>
            )}
          </div>
        </>,
        document.body
      )}

      {/* Full notification detail modal (portaled to body so it centres on the viewport) */}
      {selectedNotif && createPortal(
        <div className="nd-detail-overlay" onClick={() => setSelectedNotif(null)}>
          <div className="nd-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="nd-detail-head">
              <h3><i className="fas fa-bell" /> Notification</h3>
              <button className="nd-detail-close" onClick={() => setSelectedNotif(null)} aria-label="Close">
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="nd-detail-body">
              <h4 className="nd-title">{selectedNotif.title || 'Notification'}</h4>
              {(selectedNotif.type || selectedNotif.priority) && (
                <div className="nd-badges">
                  {selectedNotif.type && <span className="nd-badge">{String(selectedNotif.type).replace(/_/g, ' ')}</span>}
                  {selectedNotif.priority ? <span className="nd-badge prio">Priority {selectedNotif.priority}</span> : null}
                </div>
              )}
              <p className="nd-detail-msg">{selectedNotif.message || selectedNotif.body || 'No additional details.'}</p>
              <div className="nd-detail-meta">
                {formatTime(selectedNotif.timestamp || selectedNotif.created_at)}
                {selectedNotif.related_id && <span> · Ref #{String(selectedNotif.related_id).slice(-8).toUpperCase()}</span>}
              </div>
            </div>
            <div className="nd-detail-foot">
              <button className="nd-del" onClick={() => { const id = selectedNotif._id || selectedNotif.id; if (id) deleteNotification(id); setSelectedNotif(null); }}>
                <i className="fas fa-trash" style={{ marginRight: '0.4rem' }} />Delete
              </button>
              <button className="nd-close" onClick={() => setSelectedNotif(null)}>Close</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationsDropdown;
